import { FirebaseMessaging } from '@capacitor-firebase/messaging'

import {
  CREATE_SESSION_PATH,
  playIntentLocationState,
  prefillFromPushData,
} from '@/features/play-intents/lib/playIntentPrefill'
import { httpClient } from '@/shared/api/httpClient'
import { clientPlatform, isNative } from '@/shared/api/platform'
import { safeInternalPath } from '@/shared/lib/safeInternalPath'

/**
 * Registro de push nativo (FCM vía @capacitor-firebase/messaging). Todo es no-op en web.
 *
 * Import ESTÁTICO del plugin: el `import()` dinámico se cuelga en la WebView nativa (no
 * resuelve ni falla). No infla el bundle web porque la implementación web del plugin (que
 * es la que arrastra `firebase`) se carga de forma lazy y en web nunca se invoca (guard
 * isNative). Flujo: pedir permiso → token FCM → `POST /me/device-tokens` + tap→deep-link.
 * Ver docs/plans/2026-09-03-push-nativo.md.
 */

let registered = false
let lastToken: string | null = null

export async function registerPush(): Promise<void> {
  if (!isNative() || registered) return
  registered = true
  try {
    const messaging = FirebaseMessaging
    // Android 8+: sin un canal explícito el push en background cae a un fallback de baja
    // prioridad (sin heads-up). Creamos "matchplay_default" (importance HIGH) — el mismo id
    // que el meta-data del manifest y el AndroidConfig del backend. No-op/inaplicable en iOS.
    if (clientPlatform() === 'android') {
      try {
        await messaging.createChannel({
          id: 'matchplay_default',
          name: 'Notificaciones',
          importance: 5,
          visibility: 1,
        })
      } catch (e) {
        console.error('[push] createChannel ERROR:', e)
      }
    }
    const permission = await messaging.requestPermissions()
    if (permission.receive !== 'granted') {
      registered = false
      return
    }
    await messaging.addListener('tokenReceived', (event) => {
      void sendToken(event.token)
    })
    await messaging.addListener('notificationActionPerformed', (event) => {
      void handleTap(event.notification?.data)
    })
    const { token } = await messaging.getToken()
    await sendToken(token)
  } catch (e) {
    // best-effort: un fallo no rompe la app, pero lo logueamos para poder depurar.
    console.error('[push] registerPush ERROR:', e)
    registered = false
  }
}

export async function unregisterPush(): Promise<void> {
  if (!isNative()) return
  registered = false
  const token = lastToken
  lastToken = null
  try {
    await FirebaseMessaging.removeAllListeners()
    if (token) {
      await httpClient.delete('/me/device-tokens', { data: { token } })
    }
  } catch {
    /* noop */
  }
}

async function sendToken(token: string | undefined): Promise<void> {
  const platform = clientPlatform()
  if (!token || !platform || token === lastToken) return
  lastToken = token
  try {
    await httpClient.post('/me/device-tokens', { token, platform: platform.toUpperCase() })
  } catch (e) {
    console.error('[push] POST /me/device-tokens ERROR:', e)
    lastToken = null // reintentará en el próximo tokenReceived / registro
  }
}

/**
 * Tap en la notif → mismo destino que el clic en la campana:
 * - `PLAY_INTENT_GROUP` ("Ya sois N con ganas…") y `PLAY_INTENT_CHAT` (mensaje en el chat
 *   del grupo) → el chat del grupo, por las ganas del destinatario (spec 2026-10-12). Un
 *   `PLAY_INTENT_GROUP` sin ganas → asistente de crear partida prerrellenado (`data`).
 * - `COMMUNICATION` (envío de Comunicaciones, sin campana) → su `path`, solo si es una ruta
 *   interna (`safeInternalPath`); si no, abre la app sin navegar.
 * - Resto → la sesión (`data.sessionId`).
 */
async function handleTap(data: unknown): Promise<void> {
  const payload = (data ?? {}) as Record<string, unknown>
  if (payload.type === 'COMMUNICATION') {
    const path = safeInternalPath(payload.path)
    if (path) {
      const { router } = await import('@/app/router')
      await router.navigate(path)
    }
    return
  }
  const groupChatIntent =
    payload.type === 'PLAY_INTENT_CHAT'
      ? payload.intentId
      : payload.type === 'PLAY_INTENT_GROUP'
        ? payload.playIntentId
        : undefined
  if (typeof groupChatIntent === 'string' && /^\d+$/.test(groupChatIntent)) {
    const { router } = await import('@/app/router')
    await router.navigate(`/messages/group/${groupChatIntent}`)
    return
  }
  if (payload.type === 'PLAY_INTENT_GROUP') {
    const prefill = prefillFromPushData(payload)
    const { router } = await import('@/app/router')
    await router.navigate(
      CREATE_SESSION_PATH,
      prefill ? { state: playIntentLocationState(prefill) } : undefined,
    )
    return
  }
  const sessionId = payload.sessionId
  if (typeof sessionId === 'string' && sessionId) {
    const { router } = await import('@/app/router')
    await router.navigate(`/sessions/${sessionId}`)
  }
}
